// 拓展 three.js 原有的 Object3D 能力，静态方法集合

import { BufferGeometry, Line, Material, Mesh, MeshBasicMaterial, Object3D, RingGeometry } from 'three';

import { CSS2DObject, FatLine, LineSegmentsMesh } from '@/lib/three';

export class EnhancedObject3D {
  /**
   * 向 target 的 children 中特定位置插入一个 Object3D 对象，会改变原 target
   * @param target
   * @param object 待插入对象
   * @param index 插入后的位置
   * @returns target
   */
  static insertChildren(target: Object3D, object: Object3D, index: number) {
    if (index === target.children.length) {
      return target.add(object);
    }
    if (index >= 0 && index < target.children.length) {
      if (object === target) {
        console.error("THREE.Object3D.add: object can't be added as a child of itself.", object);
        return target;
      }

      if (object && object.isObject3D) {
        if (object.parent !== null) {
          object.parent.remove(object);
        }

        object.parent = target;
        target.children.splice(index, 0, object);

        object.dispatchEvent({ type: 'added' });
      } else {
        console.error('THREE.Object3D.add: object not an instance of THREE.Object3D.', object);
      }

      return target;
    }
  }

  /**
   * 递归查找列表中的 LineSegmentsMesh 对象，并返回第一个找到的对象；若无，则返回 null
   * @param list
   * @returns
   */
  static findLineObject(list: Object3D[]): FatLine | LineSegmentsMesh | Mesh<RingGeometry, MeshBasicMaterial> | null {
    if (list.length === 0) return null;

    for (let i = 0, l = list.length; i < l; i++) {
      // FatLine 继承自 LineSegmentsMesh
      if (list[i] instanceof LineSegmentsMesh) {
        return list[i] as FatLine | LineSegmentsMesh;
      } else {
        const line = EnhancedObject3D.findLineObject(list[i].children);
        if (line !== null) return line;
      }
    }

    for (let i = 0, l = list.length; i < l; i++) {
      const target = list[i];
      if (target instanceof Mesh && target.geometry instanceof RingGeometry) {
        return list[i] as Mesh<RingGeometry, MeshBasicMaterial>;
      } else {
        const line = EnhancedObject3D.findLineObject(list[i].children);
        if (line !== null) return line;
      }
    }

    return null;
  }

  /**
   * 更新目标对象的边框颜色
   */
  static setBorderColor(target: Object3D, color: string) {
    const border = EnhancedObject3D.findLineObject([target]);

    if (border !== null && 'color' in border.material) {
      (border.material as MeshBasicMaterial).color?.set(color);
    }
  }

  /**
   * 更新目标对象的边框粗细
   */
  static setBorderThickness(target: Object3D, thickness: number) {
    const border = EnhancedObject3D.findLineObject([target]);

    // 只有 LineSegmentsMesh 可以设置 linewidth
    if (border instanceof LineSegmentsMesh) {
      border.material.linewidth = thickness;
    }
  }

  /**
   * 重置目标对象的边框颜色
   */
  static resetBorderColor(target: Object3D) {
    // 判断目标对象的材质上是否存在颜色值
    const targetMaterial = (target as Mesh<BufferGeometry, MeshBasicMaterial>).material;
    if (targetMaterial instanceof Material && target.userData.color) {
      const border = EnhancedObject3D.findLineObject([target]);

      if (border !== null && 'color' in border.material) {
        const color = target.userData.color;

        (border.material as MeshBasicMaterial).color.set(color);
        border.children.forEach((child) => {
          const material = (child as Mesh<BufferGeometry, MeshBasicMaterial>).material;
          if (material && material.color) {
            material.color.set(color);
          }
        });
      }
    }
  }

  /**
   * 设置目标对象的整体颜色
   * @param target 目标对象
   * @param color 修改的颜色值
   * @param opacity 需要在对象原来的 opacity 基础上乘以的值
   */
  static setObjectColor(target: Object3D, color: string, opacity: number = 1) {
    target.traverse((object) => {
      if ((object instanceof Mesh || object instanceof Line) && 'color' in object.material) {
        object.material.color.set(color);
        object.material.opacity = opacity * object.userData.opacity;
      }
    });
  }

  /**
   * 设置目标对象的整体透明度
   * @param target 目标对象
   * @param opacity 需要在对象原来的 opacity 基础上乘以的值
   */
  static setObjectOpacity(target: Object3D, opacity: number) {
    target.traverse((object) => {
      if ((object instanceof Mesh || object instanceof Line) && 'opacity' in object.material) {
        object.material.opacity = opacity * object.userData.opacity;
      }
    });
  }

  /**
   * 重置目标对象的整体颜色
   * @param target 目标对象
   */
  static resetObjectColor(target: Object3D) {
    if ((target instanceof Mesh || target instanceof Line) && target.userData.color) {
      const color = target.userData.color;
      EnhancedObject3D.setObjectColor(target, color);
    }
  }

  /**
   * 显示或隐藏属性标签
   * @param target 目标对象
   * @param visible 标签是否可见
   */
  static toggleLabelVisibility(target: Object3D, visible: boolean) {
    const obj = target.children.find((obj) => obj instanceof CSS2DObject);
    if (obj) {
      obj.visible = visible;
    }
  }
}
